import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { AlertCircle, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";

// CC-158 (2026-10-05) — a safe deep-link target is a same-origin app
// path only. Reject anything that looks like a scheme-relative or
// absolute URL so a crafted ?next= / state.from can't open-redirect.
function _safeDeepLink(target) {
  if (typeof target !== "string" || !target) return null;
  // Must start with a single leading slash and NOT look like a
  // scheme-relative URL ("//evil.com/x") or an absolute URL
  // ("http://evil.com/x"). React Router paths always begin with "/".
  if (!target.startsWith("/")) return null;
  if (target.startsWith("//")) return null;
  if (target.includes("://")) return null;
  return target;
}

export default function Login() {
  const { slug } = useParams();
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // CC-158 — if ProtectedRoute bounced the user here with a deep link,
  // prefer that as the post-login destination over the role-based default.
  const _deepLink = _safeDeepLink(location.state && location.state.from);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [scopedTenant, setScopedTenant] = useState(null);

  useEffect(() => {
    if (!slug) { setScopedTenant(null); return; }
    api.get(`/tenants/by-slug/${slug}`)
      .then(({ data }) => setScopedTenant(data))
      .catch(() => setScopedTenant({ error: true, slug }));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const result = await login(email.trim(), password, { slug });
      // CC-158 — if ProtectedRoute captured a deep link, honor it over
      // the role-based default. Allowlisted to same-origin app paths by
      // `_safeDeepLink` above; a crafted scheme-relative or absolute URL
      // falls through to the role-based default.
      if (_deepLink) {
        nav(_deepLink);
        return;
      }
      // Redirect based on role + URL context
      if (result.role === "platform_admin") {
        // If admin logged in at /{slug}/login, respect that context; else admin home
        nav(slug ? `/${slug}/app/overview` : "/admin/tenants");
      } else {
        // Tenant user: send them to their tenant's dashboard.
        // If they logged in at /{slug}/login and their tenant matches the slug, use that.
        // Otherwise use their real tenant's slug (looked up from /tenants/current).
        try {
          const { data: t } = await api.get("/tenants/current");
          if (slug && t?.slug && t.slug !== slug) {
            setErr(`Wrong login for this tenant — this URL is for ${scopedTenant?.business_name || slug}. Redirecting to your workspace…`);
          }
          nav(`/${t?.slug || slug}/app/overview`);
        } catch {
          nav(slug ? `/${slug}/app/overview` : "/");
        }
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (em, pw) => { setEmail(em); setPassword(pw); };
  // 2026-08-15: `showDemo` retired with the plaintext-password quick-
  // login panel (see the removed block below in the JSX). Kept the
  // constant declaration OUT so a copy-paste re-introduction doesn't
  // silently re-enable the leak.
  //   const showDemo = process.env.REACT_APP_SHOW_DEMO_ACCOUNTS === "true";

  return (
    <div className="cc-landing min-h-screen flex flex-col" data-testid="login-page">
      <div className="max-w-[1200px] w-full mx-auto px-8 py-5 flex items-center justify-between">
        <Link to="/"><Logo withWordmark tone="dark" /></Link>
        <Link to="/" className="text-[12.5px] text-[color:var(--cc-navy-muted)] hover:text-white">
          ← Back to home
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[440px]">
          <div className="text-[11px] uppercase tracking-[.18em] text-[#ffc72c] font-semibold mb-3">
            {slug ? "Tenant sign-in" : "Franchise sign-in"}
          </div>
          <h1 className="font-display font-extrabold text-[34px] leading-tight tracking-tight text-white">
            {scopedTenant && !scopedTenant.error ? scopedTenant.business_name : "Welcome back."}
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-navy-muted)] mt-2">
            {scopedTenant && !scopedTenant.error
              ? `${scopedTenant.location} · /${scopedTenant.slug}`
              : "Your tenant workspace, agent roster, and KPIs — behind one login."}
          </p>
          {scopedTenant?.error && (
            <div className="mt-3 text-[12px] text-[#ff9d9d] bg-[#3a1414] border border-[#5c1e1e] rounded-md px-3 py-2" data-testid="unknown-tenant-error">
              No tenant found at <span className="font-mono-data">/{slug}</span>. Check the URL or use the universal sign-in.
            </div>
          )}

          <form
            onSubmit={submit}
            className="mt-6 bg-[color:var(--cc-navy-card)]/70 backdrop-blur border border-[color:var(--cc-navy-border)] rounded-2xl p-6"
            data-testid="login-form"
          >
            <label className="block text-[11px] uppercase tracking-[.12em] text-[color:var(--cc-navy-muted)] font-semibold mb-2">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="w-full bg-[#0a0f1a] border border-[color:var(--cc-navy-border)] rounded-lg px-3.5 py-3 text-[14px] text-white outline-none focus:border-[#0072bc] transition-colors"
              placeholder="you@franchise.com"
            />

            <label className="block text-[11px] uppercase tracking-[.12em] text-[color:var(--cc-navy-muted)] font-semibold mt-4 mb-2">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full bg-[#0a0f1a] border border-[color:var(--cc-navy-border)] rounded-lg px-3.5 py-3 text-[14px] text-white outline-none focus:border-[#0072bc] transition-colors"
              placeholder="••••••••"
            />

            {err && (
              <div className="mt-4 flex items-start gap-2 text-[12.5px] text-[#ff9d9d] bg-[#3a1414] border border-[#5c1e1e] rounded-md px-3 py-2" data-testid="login-error">
                <AlertCircle size={14} strokeWidth={1.8} className="mt-[1px] shrink-0" />
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="cc-btn-primary w-full justify-center mt-6 disabled:opacity-60"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={16} strokeWidth={1.8} /></>)}
            </button>
            <div className="mt-3 text-center">
              <Link
                to="/forgot-password"
                data-testid="login-forgot-password-link"
                className="text-[12.5px] text-[color:var(--cc-navy-muted)] hover:text-[#4aa4ff]"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          {/* Demo-accounts quick-login panel REMOVED 2026-08-15 per Brett
              (security incident): the panel used to render email +
              PLAINTEXT PASSWORD as clickable buttons, gated by
              REACT_APP_SHOW_DEMO_ACCOUNTS=true. The gate is build-time
              (CRA inlines the value), so once prod was built with that
              env, anyone visiting /login could copy the platform-admin
              password out of the rendered HTML and log in as Brett.
              The env var has also been removed from frontend/.env as
              belt-and-suspenders — do NOT re-introduce this pattern.
              If a demo landing page is needed later, use the /demo
              route (Demo.jsx) which posts credentials to a server-side
              endpoint rather than exposing them in the client bundle. */}
        </div>
      </div>
    </div>
  );
}
